import { useFlag } from '@unleash/proxy-client-react'
import { useApolloClient, useLazyQuery } from '@apollo/client'
import CartIcon from '@mui/icons-material/ShoppingCartOutlined'
import { IconButton, MenuItem, Tooltip } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BucketMedia, BucketMediaByIdsDocument, BucketMediaFragmentFragmentDoc } from '../../../../../graphql/generated'
import useAlerts from '../../../hooks/useAlerts'
import useSelectedMedia from '../../../hooks/useSelectedMedia'

interface Props {
  media?: BucketMedia | null
  isWithinOverflowMenu?: boolean
}
export default function MediaPrintShop({ media = null, isWithinOverflowMenu = false }: Readonly<Props>) {
  const { selectedMedia, clearSelectedMedia } = useSelectedMedia()
  const selectedMediaIds = media ? [media.id] : Array.from(selectedMedia.keys())
  const { bucketId } = useParams<{ bucketId: string }>()
  const useUpdatedAlbumActions = useFlag('useUpdatedAlbumActions')
  const { createAlert } = useAlerts()
  const client = useApolloClient()
  const [heicUrls, setHeicUrls] = useState<string[]>([])

  const [query, results] = useLazyQuery(BucketMediaByIdsDocument, {
    // Direct links are signed with an expiring token. Therefore we must refetch to get a new token
    fetchPolicy: 'no-cache',
    onError: (error) => {
      if (error.graphQLErrors.length) {
        error.graphQLErrors.forEach((e) => {
          createAlert(e.message, 'error', e)
        })
        return
      }
      createAlert('There was a problem retrieving your image. Please try again later.')
    },
    onCompleted: ({ bucketMediaByIds }) => {
      if (!bucketMediaByIds) return
      const urls: string[] = []
      bucketMediaByIds.forEach((bucketMedia) => {
        const signedUrl = encodeURIComponent(bucketMedia.signedUrl || '')
        urls.push(signedUrl)
      })

      urls.push(...heicUrls)

      if (!isEmpty(urls)) {
        const url = `https://printshoplab.printshoplab.com/prints/quick-order?source=${urls.join(';')}`
        window.open(url, '_blank')
      }

      clearSelectedMedia()
    }
  })

  const handleClick = () => {
    if (!bucketId || !selectedMediaIds.length) return

    const heics: string[] = []
    const mediaIds: string[] = []

    selectedMediaIds.forEach((id) => {
      const item = client.readFragment({ id: `BucketMedia:${id}`, fragment: BucketMediaFragmentFragmentDoc })
      if (media?.imageUrl && item?.filename?.toLowerCase().includes('.heic')) {
        // heic files must use a hosting domain link rather than a direct link
        const url = media.imageUrl
        heics.push(encodeURIComponent(url))
      } else if (!item?.isVideo) {
        mediaIds.push(id)
      }
    })

    setHeicUrls(heics)

    query({ variables: { bucketId, mediaIds } })
  }

  return (
    <>
      {isWithinOverflowMenu ? (
        <MenuItem color="primary" onClick={handleClick} aria-label="Buy Prints" id="order-prints-menu-item" data-test="order-prints-menu-item">
          <CartIcon style={{ marginRight: 8 }} /> Buy Prints
        </MenuItem>
      ) : (
        <Tooltip title={selectedMedia.size ? 'Buy Prints' : 'No Media Selected'} arrow>
          <div>
            <IconButton
              aria-label="Buy Prints"
              onClick={handleClick}
              color="primary"
              data-test="order-prints"
              id="order-prints-button"
              sx={{ padding: useUpdatedAlbumActions ? '4px' : '8px' }}
            >
              {results.loading ? <CircularProgress size={20} /> : <CartIcon />}
            </IconButton>
          </div>
        </Tooltip>
      )}
    </>
  )
}
